import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ultraadminApi, SchoolsStatsResponse, SchoolStatsItem, SchoolFilterOptions } from '@/services/api/ultraadmin'
import { Card, Button, LoadingSpinner, Input, Select, Pagination } from '@/components/common'
import { Building2, Users, List, Layers, X } from 'lucide-react'

export function UltraadminStatisticsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // Filter state from URL
  const [filterName, setFilterName] = useState(searchParams.get('filter_name') || '')
  const [filterCountry, setFilterCountry] = useState(searchParams.get('filter_country') || '')
  const [filterCity, setFilterCity] = useState(searchParams.get('filter_city') || '')
  const [filterZipCode, setFilterZipCode] = useState(searchParams.get('filter_zip_code') || '')
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'name')
  const [sortOrder, setSortOrder] = useState(searchParams.get('sort_order') || 'asc')
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1') || 1)
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('page_size') || '50') || 50)
  const [groupedView, setGroupedView] = useState(true)

  // Data state
  const [summary, setSummary] = useState<{ total_schools: number; total_users: number; overdue_school_count?: number } | null>(null)
  const [schoolsData, setSchoolsData] = useState<SchoolsStatsResponse | null>(null)
  const [filterOptions, setFilterOptions] = useState<SchoolFilterOptions | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load filter options once
  useEffect(() => {
    ultraadminApi.getSchoolFilterOptions()
      .then((opts) => setFilterOptions(opts))
      .catch(() => {/* non-critical */})
  }, [])

  // Load summary once
  useEffect(() => {
    ultraadminApi.getDashboardStats()
      .then((d) => setSummary({ total_schools: d.total_schools, total_users: d.total_users, overdue_school_count: d.overdue_school_count }))
      .catch(() => {/* non-critical */})
  }, [])

  // Load schools data
  const loadSchools = useCallback(() => {
    setLoading(true)
    setError(null)
    const params: Record<string, string | number> = {
      sort_by: sortBy,
      sort_order: sortOrder,
      skip: (page - 1) * pageSize,
      limit: pageSize,
    }
    if (filterName) params.filter_name = filterName
    if (filterCountry) params.filter_country = filterCountry
    if (filterCity) params.filter_city = filterCity
    if (filterZipCode) params.filter_zip_code = filterZipCode

    ultraadminApi.getSchoolsWithStats(params)
      .then((data) => { setSchoolsData(data); setError(null) })
      .catch((err: any) => {
        setError(err?.detail || 'Daten konnten nicht geladen werden')
        setSchoolsData(null)
      })
      .finally(() => setLoading(false))
  }, [filterName, filterCountry, filterCity, filterZipCode, sortBy, sortOrder, page, pageSize])

  useEffect(() => { loadSchools() }, [loadSchools])

  // URL sync
  useEffect(() => {
    const p = new URLSearchParams()
    if (filterName) p.set('filter_name', filterName)
    if (filterCountry) p.set('filter_country', filterCountry)
    if (filterCity) p.set('filter_city', filterCity)
    if (filterZipCode) p.set('filter_zip_code', filterZipCode)
    if (sortBy !== 'name') p.set('sort_by', sortBy)
    if (sortOrder !== 'asc') p.set('sort_order', sortOrder)
    if (page > 1) p.set('page', String(page))
    if (pageSize !== 50) p.set('page_size', String(pageSize))
    setSearchParams(p, { replace: true })
  }, [filterName, filterCountry, filterCity, filterZipCode, sortBy, sortOrder, page, pageSize, setSearchParams])

  const resetFilters = () => {
    setFilterName('')
    setFilterCountry('')
    setFilterCity('')
    setFilterZipCode('')
    setSortBy('name')
    setSortOrder('asc')
    setPage(1)
  }

  // Grouped view data
  const groupedSchools = schoolsData?.schools.reduce((acc, school) => {
    const country = school.country || 'Unbekannt'
    if (!acc[country]) acc[country] = { schools: [] as SchoolStatsItem[], userCount: 0 }
    acc[country].schools.push(school)
    acc[country].userCount += school.user_count
    return acc
  }, {} as Record<string, { schools: SchoolStatsItem[]; userCount: number }>) || {}

  const countryOptions = filterOptions?.countries.map((c) => ({ value: c, label: c })) || []
  const cityOptions = filterOptions?.cities.map((c) => ({ value: c, label: c })) || []

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'country', label: 'Land' },
    { value: 'city', label: 'Stadt' },
    { value: 'zip_code', label: 'PLZ' },
    { value: 'user_count', label: 'User-Anzahl' },
  ]

  if (loading && !schoolsData) {
    return <div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Statistik</h1>
        <p className="mt-1 text-gray-500">Übersicht aller Schulen und User</p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => { setError(null); loadSchools() }} className="text-red-500 hover:text-red-700">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <div className="p-4 bg-purple-50 flex items-center gap-3">
            <Building2 className="text-purple-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Anzahl Schulen</p>
              <p className="text-2xl font-bold text-purple-700">
                {(summary?.total_schools ?? 0).toLocaleString('de-CH')}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4 bg-indigo-50 flex items-center gap-3">
            <Users className="text-indigo-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">Anzahl User</p>
              <p className="text-2xl font-bold text-indigo-700">
                {(summary?.total_users ?? 0).toLocaleString('de-CH')}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <button type="button" className="w-full text-left" onClick={() => navigate('/ultraadmin/overdue')}>
            <div className="p-4 bg-amber-50 flex items-center gap-3">
              <Building2 className="text-amber-600" size={24} />
              <div>
                <p className="text-sm text-gray-600">Schulen im Verzug</p>
                <p className="text-2xl font-bold text-amber-700">
                  {(summary?.overdue_school_count ?? 0).toLocaleString('de-CH')}
                </p>
              </div>
            </div>
          </button>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Filter</h2>
            <button onClick={resetFilters} className="text-sm text-blue-600 hover:underline">
              Zurücksetzen
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              label="Name"
              value={filterName}
              onChange={(e) => { setFilterName(e.target.value); setPage(1) }}
              placeholder="Schulname suchen…"
            />
            <Select
              label="Land"
              value={filterCountry}
              onChange={(e) => { setFilterCountry(e.target.value); setPage(1) }}
              options={[{ value: '', label: 'Alle Länder' }, ...countryOptions]}
            />
            <Select
              label="Stadt"
              value={filterCity}
              onChange={(e) => { setFilterCity(e.target.value); setPage(1) }}
              options={[{ value: '', label: 'Alle Städte' }, ...cityOptions]}
            />
            <Input
              label="PLZ"
              value={filterZipCode}
              onChange={(e) => { setFilterZipCode(e.target.value); setPage(1) }}
              placeholder="PLZ…"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Select
              label="Sortieren nach"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              options={sortOptions}
            />
            <Select
              label="Richtung"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              options={[
                { value: 'asc', label: 'Aufsteigend ↑' },
                { value: 'desc', label: 'Absteigend ↓' },
              ]}
            />
          </div>
        </div>
      </Card>

      {/* View Toggle */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={groupedView ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setGroupedView(true)}
        >
          <Layers size={16} className="mr-1" /> Gruppiert nach Land
        </Button>
        <Button
          variant={!groupedView ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setGroupedView(false)}
        >
          <List size={16} className="mr-1" /> Flache Liste
        </Button>
      </div>

      {/* Loading indicator */}
      {loading && <div className="flex justify-center py-4"><LoadingSpinner size="md" /></div>}

      {/* Flat Table */}
      {!groupedView && !loading && schoolsData && (
        <Card className="mb-4">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">Schule</th>
                  <th className="text-left p-3 font-medium text-gray-600">Stadt</th>
                  <th className="text-left p-3 font-medium text-gray-600 hidden sm:table-cell">PLZ</th>
                  <th className="text-left p-3 font-medium text-gray-600 hidden md:table-cell">Land</th>
                  <th className="text-right p-3 font-medium text-gray-600">User</th>
                  <th className="text-center p-3 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {schoolsData.schools.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-gray-500">Keine Schulen gefunden</td>
                  </tr>
                ) : schoolsData.schools.map((school) => (
                  <tr key={school.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <span className="font-medium">{school.name}</span>
                      {school.short_name && (
                        <span className="ml-2 text-sm text-gray-500">({school.short_name})</span>
                      )}
                    </td>
                    <td className="p-3 text-sm">{school.city || '—'}</td>
                    <td className="p-3 text-sm hidden sm:table-cell">{school.zip_code || '—'}</td>
                    <td className="p-3 text-sm hidden md:table-cell">{school.country}</td>
                    <td className="p-3 text-right font-mono text-sm">{school.user_count.toLocaleString('de-CH')}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        school.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {school.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Grouped Table */}
      {groupedView && !loading && schoolsData && schoolsData.schools.length > 0 && (
        <div className="space-y-6 mb-4">
          {Object.entries(groupedSchools).map(([country, { schools, userCount }]) => (
            <Card key={country}>
              <div className="p-4 bg-gray-50 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  {country}
                  <span className="ml-2 text-sm font-normal text-gray-600">
                    ({schools.length} Schulen, {userCount.toLocaleString('de-CH')} User)
                  </span>
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-gray-600">Schule</th>
                      <th className="text-left p-3 font-medium text-gray-600">Stadt</th>
                      <th className="text-left p-3 font-medium text-gray-600 hidden sm:table-cell">PLZ</th>
                      <th className="text-right p-3 font-medium text-gray-600">User</th>
                      <th className="text-center p-3 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.map((school) => (
                      <tr key={school.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{school.name}</td>
                        <td className="p-3 text-sm">{school.city || '—'}</td>
                        <td className="p-3 text-sm hidden sm:table-cell">{school.zip_code || '—'}</td>
                        <td className="p-3 text-right font-mono text-sm">{school.user_count.toLocaleString('de-CH')}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            school.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {school.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && schoolsData && schoolsData.schools.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Keine Schulen für die gewählten Filter gefunden.
        </div>
      )}

      {/* Pagination */}
      {!loading && schoolsData && schoolsData.total > 0 && (
        <Pagination
          total={schoolsData.total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
        />
      )}
    </div>
  )
}
